import { amenitiesObject, exchangeRates, monthNames, shortMonthNames } from 'src/utils/const';
import {
  convertCurrency,
  formatCurrency,
  getMlaMonthName,
  isEmpty,
  rechartsToSVG,
} from 'src/utils/functions';
import SHA256 from 'crypto-js/sha256';

const convertAndFormatCurrency = (currency, value) => {
  return formatCurrency(String(convertCurrency(currency, value)));
};

const formatTop3SubmarketsAndItsChart = (currency, top3Submarkets) => {
  const currencySymbol = exchangeRates[currency].symbol;

  if (!top3Submarkets) return { chartData: '', formattedTop3SubmarketData: [] };

  const chartData = top3Submarkets.map(submarket => ({
    label: submarket.name,
    value: submarket.revenue,
  }));

  const formattedTop3SubmarketData = top3Submarkets
    .map(submarket => ({
      name: submarket.name,
      grade: submarket.neighborhood_grade,
      rank: '#' + submarket.rank,
      supply: formatCurrency(String(submarket.supply)),
      adr: currencySymbol + convertAndFormatCurrency(currency, submarket.adr),
      occupancy: `${submarket.occupancy_rate}%`,
      revenue: currencySymbol + convertAndFormatCurrency(currency, submarket.revenue),
      latitude: submarket.latitude,
      longitude: submarket.longitude,
    }))
    .slice(0, 3);

  return {
    chartData,
    formattedTop3SubmarketData,
  };
};

const getAmenitiesGraph = marketCache => {
  if (!marketCache || !marketCache.supply_breakdown) return null;

  const amenities = Object.keys(marketCache.supply_breakdown.amenity).map(amenity => ({
    label: amenitiesObject[amenity],
    value: marketCache.supply_breakdown.amenity[amenity],
  }));

  return amenities;
};

const parseTopCitiesGraph = guestGraphData => {
  if (!guestGraphData || !guestGraphData.cities) return null;

  const topCities = guestGraphData.cities;

  return Object.keys(topCities).map(city => ({
    label: city,
    value: topCities[city],
  }));
};

const parseTopCountriesGraph = guestGraphData => {
  if (!guestGraphData || !guestGraphData.countries) return null;

  const topCountries = guestGraphData.countries;

  return Object.keys(topCountries).map(country => ({
    label: country,
    value: topCountries[country],
  }));
};

const parseGuestLanguageGraph = guestGraphData => {
  if (!guestGraphData || !guestGraphData.Languages) return null;

  const guestLanguage = guestGraphData.Languages;

  return Object.keys(guestLanguage).map(language => ({
    label: language,
    value: guestLanguage[language],
  }));
};

const parseAvgLengthOfStayGraph = marketCache => {
  if (!marketCache || !marketCache.monthly_booking_data) return null;

  const monthlyBookingData = marketCache.monthly_booking_data;

  if (!monthlyBookingData) return null;

  return Object.keys(monthlyBookingData).map(month => ({
    label: shortMonthNames[Number(month.split('-')[1]) - 1] + ', ' + month.split('-')[0].slice(-2),
    value: monthlyBookingData[month].avg_length_of_stay,
  }));
};

const getExtraPricingGraph = marketCache => {
  if (!marketCache || !marketCache.extra_pricing) return null;

  const extraPricing = marketCache.extra_pricing;

  return Object.keys(extraPricing).map(key => ({
    name: key,
    value: extraPricing[key],
  }));
};

const parseTopHosts = (currency, topHosts) => {
  if (!topHosts || !topHosts.data) return null;

  const isUserAllowedToHostData = topHosts.isUserAllowedToHostData;
  const hostExportLimit = isUserAllowedToHostData ? 1000 : 5;

  const currencySymbol = exchangeRates[currency].symbol;

  return topHosts.data.slice(0, hostExportLimit).map(host => ({
    name: host.name,
    supply: formatCurrency(String(host.listings)),
    rating: host.rating,
    revenue: currencySymbol + convertAndFormatCurrency(currency, host.revenue),
    occupancy: host.occupancy_rate + '%',
    adr: currencySymbol + convertAndFormatCurrency(currency, host.adr),
  }));
};

export const cleanSvgForWeasyPrint = (svg: string | undefined): string => {
  if (!svg) return '';

  // 1. Remove <defs>...</defs> block entirely
  svg = svg.replace(/<defs[\s\S]*?<\/defs>/g, '');

  // 2. Replace '&#10;' (newline character entity) with space
  svg = svg.replace(/&#10;/g, ' ');

  // 3. Remove problematic attributes
  svg = svg
    .replace(/\s*dominant-baseline="[^"]*"/g, '')
    .replace(/\s*alignment-baseline="[^"]*"/g, '')
    .replace(/\s*text-anchor="[^"]*"/g, '');

  // 4. Remove inline styles (optional: remove this if you want to preserve styles)
  svg = svg.replace(/\s*style="[^"]*"/g, '');

  // 5. Remove empty <tspan> and <text> tags
  svg = svg.replace(/<tspan[^>]*>\s*<\/tspan>/g, '');
  svg = svg.replace(/<text[^>]*>\s*<\/text>/g, '');

  // 6. Trim leading/trailing whitespace
  return svg.trim();
};

const svgToBase64 = (svg: string | undefined): string => {
  if (!svg) return '';
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const getLatestMonth = marketDataCache => {
  try {
    const yearMonth = Object.keys(marketDataCache.ltm_monthly_performance_breakdown.bedroom['1'])
      .sort()
      .reverse()[0];
    const month = parseInt(yearMonth.split('-')[1]) - 1 + '';
    const year = yearMonth.split('-')[0];
    return `${year} ${monthNames[month]}`;
  } catch (error) {
    return '';
  }
};

const getFilterFilenameString = (filter: any): string => {
  const stableFormat = (value: any): string => {
    if (value === null || typeof value !== 'object') {
      return String(value);
    }

    if (Array.isArray(value)) {
      const isPrimitive = value.every(v => typeof v !== 'object');
      const sorted = isPrimitive ? [...value].sort() : value;
      return sorted.map(stableFormat).join('-');
    }

    const sortedKeys = Object.keys(value).sort();
    const entries = sortedKeys
      .map(key => {
        const val = stableFormat(value[key]);
        return val === '' ? '' : `${key}_${val}`;
      })
      .filter(Boolean);

    return entries.join('__').toLowerCase();
  };

  // Final sanitation pass: replace unsafe filename/url characters
  const sanitize = (str: string): string => str.replace(/[^\w.-]/g, '_'); // keeps letters, numbers, underscore, dot, dash

  return sanitize(stableFormat(filter));
};

export function generateDeterministicHash(input: string): string {
  return SHA256(input).toString(); // Hex string
}

export const parseMarketDataForPDFRequest = (
  currency,
  marketDetails,
  marketGradeInfo,
  marketDataCache,
  marketTrend,
  marketSeasonality,
  revenueFormattedChartData,
  formattedSubmarketSummary,
  top3SubmarketsRef,
  submarketSortKey,
  guestGraphData,
  topHosts,
  topSubMarketChartRef,
  demandByAmenitiesChartRef,
  mainKpiRevenueChartRef,
  bestPropertyTypeGraphRef,
  listingByPropertySizeGraphRef,
  listingByPropertyTypeGraphRef,
  cancellationPolicyGraphRef,
  listingsByDaysAvailableGraphRef,
  listingsByCapacityGraphRef,
  domesticVsInternationalGraphRef,
  monthlyAlosDataGraphRef,
  monthlyBltDataGraphRef,
  occupancyRateCalendarDataHistorical,
  occupancyRateCalendarDataPrelim,
  occupancyRateCalendarDataFuture,
  dailyOccupancyRateCalendarHistoricalGraphRef,
  dailyOccupancyRateCalendarFutureGraphRef,
  filterVal,
  filterActive,
  isSubmarket,
  user,
  isFullHostUnlocked,
  reportMarketHeader,
  configurableFilterBarCardSelectionModalRef
) => {
  // Disabled for faster PDF generation (WeasyPrint is slow on calendar + host sections)
  const settings = {
    calendar: false,
    host: false,
  };

  const latestMonth = getLatestMonth(marketDataCache);
  const filterStringKey = filterActive ? getFilterFilenameString(filterVal) : '';
  const uniquePdfString = `${marketDetails.id}_${filterStringKey}_${latestMonth.replace(
    / /g,
    '_'
  )}`.toLowerCase();
  const uniquePdfHash = generateDeterministicHash(uniquePdfString);

  const metadata = {
    market_id: marketDetails.id,
    name: marketDetails.name,
    city: marketDetails.city,
    country: marketDetails.country,
    country_code: marketDetails.countryCode,
    latest_month: latestMonth,
    filter_string_key: filterStringKey,
    filter: filterActive ? filterVal : {},
    is_filter_active: filterActive,
    is_submarket: isSubmarket,
    created_date: new Date().toISOString().slice(0, 10),
    is_full_host_unlocked: isFullHostUnlocked,
    report_market_header: reportMarketHeader,
    unique_pdf_hash: uniquePdfHash,
    unique_market_filter_month_string: uniquePdfString,
  };

  const market_grade = {
    grade: marketGradeInfo?.grade || '',
    summary: marketGradeInfo?.summary || [],
  };

  const ltmPerformance = marketDataCache.ltm_performance;
  const revenueYoYChange =
    marketDataCache.yearly_performance_changes.one_year_changes.revenue_changes;
  const currencySymbol = exchangeRates[currency].symbol;

  const kpi = {
    revenue: {
      value: currencySymbol + convertAndFormatCurrency(currency, ltmPerformance.revenue),
      yoy_change: revenueYoYChange + '%',
    },
    occupancy: {
      value: ltmPerformance.occupancy_rate + '%',
    },
    nightlyRate: {
      value: currencySymbol + convertAndFormatCurrency(currency, ltmPerformance.adr),
    },
    activeListings: {
      value: formatCurrency(String(ltmPerformance.supply)),
    },
    seasonality: {
      value: marketSeasonality,
    },
    trend: {
      value: marketTrend + '%',
    },
  };

  const revenue_seasonality_graph = revenueFormattedChartData;

  const { chartData, formattedTop3SubmarketData } = formatTop3SubmarketsAndItsChart(
    currency,
    top3SubmarketsRef.chartData
  );
  const submarkets = {
    summary: formattedSubmarketSummary,
    submarkets_vs_median_graph: chartData,
    top_submarkets: formattedTop3SubmarketData,
    top_submarkets_sorted_by: submarketSortKey,
    top_sub_markets_chart_svg: topSubMarketChartRef ? rechartsToSVG(topSubMarketChartRef) : null,
  };

  const property_types = {
    amenities: getAmenitiesGraph(marketDataCache),
    demand_by_amenities_chart_svg: demandByAmenitiesChartRef
      ? rechartsToSVG(demandByAmenitiesChartRef)
      : null,
    best_property_type_graph_svg: bestPropertyTypeGraphRef
      ? rechartsToSVG(bestPropertyTypeGraphRef)
      : null,
    best_property_type_graph_config: configurableFilterBarCardSelectionModalRef.current,
    listing_by_property_size_graph_svg: listingByPropertySizeGraphRef
      ? rechartsToSVG(listingByPropertySizeGraphRef)
      : null,
    listing_by_property_type_graph_svg: listingByPropertyTypeGraphRef
      ? svgToBase64(rechartsToSVG(listingByPropertyTypeGraphRef))
      : null,
    cancellation_policy_graph_svg: cancellationPolicyGraphRef
      ? svgToBase64(rechartsToSVG(cancellationPolicyGraphRef))
      : null,
    supply_by_days_available_graph_svg: listingsByDaysAvailableGraphRef
      ? rechartsToSVG(listingsByDaysAvailableGraphRef)
      : null,
    supply_by_capacity_graph_svg: listingsByCapacityGraphRef
      ? rechartsToSVG(listingsByCapacityGraphRef)
      : null,
  };

  const guests = {
    top_cities_graph: parseTopCitiesGraph(guestGraphData),
    top_countries_graph: parseTopCountriesGraph(guestGraphData),
    top_languages_graph: parseGuestLanguageGraph(guestGraphData),
    length_of_stay_graph: parseAvgLengthOfStayGraph(marketDataCache),
    domestic_vs_international_graph_svg: domesticVsInternationalGraphRef
      ? rechartsToSVG(domesticVsInternationalGraphRef)
      : null,
    monthly_avg_length_of_stay_graph_svg: monthlyAlosDataGraphRef
      ? rechartsToSVG(monthlyAlosDataGraphRef)
      : null,
    monthly_booking_lead_time_graph_svg: monthlyBltDataGraphRef
      ? rechartsToSVG(monthlyBltDataGraphRef)
      : null,
  };

  const others = {
    extra_pricing: getExtraPricingGraph(marketDataCache),
    top_hosts: settings.host ? parseTopHosts(currency, topHosts) : null,
  };

  const daily_calendar = {
    historical_graph: occupancyRateCalendarDataHistorical,
    prelim_graph: occupancyRateCalendarDataPrelim,
    future_graph: occupancyRateCalendarDataFuture,
    historical_graph_svg: dailyOccupancyRateCalendarHistoricalGraphRef
      ? rechartsToSVG(dailyOccupancyRateCalendarHistoricalGraphRef)
      : null,
    future_graph_svg: dailyOccupancyRateCalendarFutureGraphRef
      ? rechartsToSVG(dailyOccupancyRateCalendarFutureGraphRef)
      : null,
  };

  const finalResult = {
    settings,
    pdf_payload: {
      metadata,
      market_overview: {
        market_grade,
        kpi,
        revenue_seasonality_graph,
        main_kpi_revenue_chart_svg: mainKpiRevenueChartRef
          ? rechartsToSVG(mainKpiRevenueChartRef)
          : null,
      },
      submarkets,
      property_types,
      guests,
      others,
      daily_calendar,
    },
  };

  console.log(finalResult);

  return finalResult;
};
